import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { formatUnits, getAddress } from 'viem';
import {
    switchChain,
    writeContract,
} from 'wagmi/actions';

import { toast } from '@/components/ui/toast';
import {
    HYPER_EVM_CHAIN_ID,
    useAnchorChain,
    useAuthedApi,
    useFactionsAuthedApi,
    useWallet,
    walletWagmiConfig,
} from '@/features/wallet';
import { wolfswapFactionsApi } from '@/lib/api';
import {
    fetchBalanceLight,
    fetchSpeedConfig,
    reportAgentPruned,
} from '@/lib/api/speed';
import { describeErrorForUser, userErrorText } from '@/lib/errors';
import { queryKeys } from '@/lib/query';

import { readOnchainBalance } from '../../wallet/services/onchainBalance';
import {
    CORE_SWEEP_GAS_BUFFER_8,
    CUSTODIAL_WALLET_WITHDRAW_ABI,
    DIRECT_STEP_LABELS,
    directWithdrawBlocker,
    HYPER_EVM_MANAGER_ABI,
    hasUsableAdapter,
    MIN_HYPE_FOR_GAS_WEI,
    planDirectWithdraw,
    readCoreSpotUsdc8,
    readEvmUsdc6,
    readHypeBalanceWei,
} from '../services/directWithdraw';
import type { DirectWithdrawStep } from '../services/directWithdraw';

import {
    ensureHyperEvmChain,
    waitForSuccessfulHyperEvmReceipt,
} from './hyperEvmChain';

/** Core credits land in a later Core block — poll each one before the next step. */
const CORE_CREDIT_TIMEOUT_MS = 90_000;
const CORE_CREDIT_INTERVAL_MS = 3_000;
const TOAST_DURATION = 10_000;

export interface DirectWithdrawState {
    status: 'idle' | 'preparing' | 'running' | 'success' | 'failed';
    /** 1-based position of the step currently executing. */
    stepIndex: number;
    stepCount: number;
    stepLabel: string | null;
    error: string | null;
    txHashes: string[];
    /** What the completed run delivered to the EOA (display). */
    deliveredUsd: number | null;
}

const INITIAL_STATE: DirectWithdrawState = {
    status: 'idle',
    stepIndex: 0,
    stepCount: 0,
    stepLabel: null,
    error: null,
    txHashes: [],
    deliveredUsd: null,
};

const sleep = async (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

async function pollUntil(
    label: string,
    check: () => Promise<boolean>,
): Promise<void> {
    const deadline = Date.now() + CORE_CREDIT_TIMEOUT_MS;
    for (;;) {
        try {
            if (await check()) return;
        } catch {
            // transient info/RPC hiccups must not abort a withdrawal mid-sequence; the
            // deadline below still bounds the wait.
        }
        if (Date.now() >= deadline) {
            throw new Error(
                `${label} did not arrive within ${CORE_CREDIT_TIMEOUT_MS / 1000}s. ` +
                    'Your funds are safe. Retry in a minute, or use the Cronos route to collect them.',
            );
        }
        await sleep(CORE_CREDIT_INTERVAL_MS);
    }
}

/**
 * EOA-direct HyperEVM withdrawal: the connected wallet signs 1–4 transactions on chain 999 that
 * move everything (custodial + adapter + HyperCore) to the user's own address. No service
 * wallet, no backend job, no bridge — see services/directWithdraw.ts for the route.
 *
 * The wallet is switched to HyperEVM for the duration and back to Cronos afterwards.
 */
export function useDirectWithdraw({ onSuccess }: { onSuccess?: () => void } = {}) {
    const [state, setState] = useState<DirectWithdrawState>(INITIAL_STATE);
    const lockRef = useRef(false);
    const { address } = useWallet();
    const { anchorChainId } = useAnchorChain();
    const factionsApi = useFactionsAuthedApi();
    const queryClient = useQueryClient();
    // the prune signal is a sensitive write, so it goes to MoonHub.API on the same authed client
    // POST /speed/agent/reactivate uses — NOT the read-only factions client above.
    const authedApi = useAuthedApi();

    const reset = useCallback(() => setState(INITIAL_STATE), []);

    // gas gate for the form: below the threshold the route cannot run, so the button is
    // disabled with an explanation instead of failing after the first signature prompt.
    const gasQuery = useQuery({
        queryKey: ['speed', 'directWithdraw', 'hypeGas', address],
        queryFn: async () => readHypeBalanceWei(address!),
        enabled: !!address,
        staleTime: 30_000,
    });
    const hypeGasOk =
        gasQuery.data === undefined
            ? null
            : gasQuery.data >= MIN_HYPE_FOR_GAS_WEI;

    const run = useCallback(async () => {
        if (lockRef.current) return;
        if (!factionsApi || !address) {
            setState((prev) => ({
                ...prev,
                status: 'failed',
                error: 'Connect & sign in first',
            }));
            return;
        }

        lockRef.current = true;
        const txHashes: string[] = [];
        try {
            setState({
                ...INITIAL_STATE,
                status: 'preparing',
                stepLabel: 'Checking your balances',
            });

            const [light, config] = await Promise.all([
                fetchBalanceLight(factionsApi),
                fetchSpeedConfig(wolfswapFactionsApi),
            ]);
            const balance = await readOnchainBalance(light);
            const plan = planDirectWithdraw({ balance, unified: light.unified });
            const blocker = directWithdrawBlocker({
                plan,
                hypeBalanceWei: await readHypeBalanceWei(address),
            });
            if (blocker === 'noFunds') {
                throw new Error('No funds available to withdraw.');
            }
            if (blocker === 'insufficientGas') {
                throw new Error(
                    'Your wallet needs a little HYPE on HyperEVM to pay gas for the direct withdrawal.',
                );
            }
            const adapter = hasUsableAdapter(light) ? getAddress(light.adapter!) : null;
            if (plan.steps.some((step) => step.kind !== 'custodialWithdraw') && !adapter) {
                // funds are reported outside the custodial wallet but no adapter exists to move
                // them — inconsistent snapshot; nothing here can act on it.
                throw new Error('Balances are settling, retry in a moment.');
            }

            // explicit switch: injected wallets are on Cronos here, and writeContract with a
            // chainId asserts rather than switches for them.
            await ensureHyperEvmChain();

            const eoa = getAddress(address);
            const usdc = getAddress(light.usdc);
            const manager = getAddress(config.manager);
            const eoaUsdcBefore = await readEvmUsdc6(usdc, eoa);

            const executeStep = async (step: DirectWithdrawStep) => {
                switch (step.kind) {
                case 'custodialWithdraw': {
                    const hash = await writeContract(walletWagmiConfig, {
                        account: eoa,
                        address: getAddress(light.custodialWallet),
                        abi: CUSTODIAL_WALLET_WITHDRAW_ABI,
                        functionName: 'withdrawErc20',
                        args: [usdc, eoa, step.amount6],
                        chainId: HYPER_EVM_CHAIN_ID,
                    });
                    txHashes.push(hash);
                    await waitForSuccessfulHyperEvmReceipt(hash, 'Custodial withdrawal');
                    return;
                }
                case 'corePerpToSpot': {
                    const spotBefore = await readCoreSpotUsdc8(adapter!);
                    const hash = await writeContract(walletWagmiConfig, {
                        account: eoa,
                        address: manager,
                        abi: HYPER_EVM_MANAGER_ABI,
                        functionName: 'selfCorePerpToSpot',
                        args: [step.notional6],
                        chainId: HYPER_EVM_CHAIN_ID,
                    });
                    txHashes.push(hash);
                    await waitForSuccessfulHyperEvmReceipt(hash, 'Trading-balance transfer');
                    await pollUntil('Your trading balance', async () =>
                        (await readCoreSpotUsdc8(adapter!)) > spotBefore);
                    return;
                }
                case 'coreSpotSweep': {
                    // the sweep amount is decided HERE, from what the class transfer
                    // actually credited, minus the transfer-gas buffer HyperCore needs.
                    const spot8 = await readCoreSpotUsdc8(adapter!);
                    const coreWei = spot8 - CORE_SWEEP_GAS_BUFFER_8;
                    if (coreWei <= 0n) return; // nothing worth sweeping — not an error
                    const adapterEvmBefore = await readEvmUsdc6(usdc, adapter!);
                    const hash = await writeContract(walletWagmiConfig, {
                        account: eoa,
                        address: manager,
                        abi: HYPER_EVM_MANAGER_ABI,
                        functionName: 'selfCoreSpotSweep',
                        args: [coreWei],
                        chainId: HYPER_EVM_CHAIN_ID,
                    });
                    txHashes.push(hash);
                    await waitForSuccessfulHyperEvmReceipt(hash, 'HyperCore sweep');
                    await pollUntil('The HyperCore transfer', async () =>
                        (await readEvmUsdc6(usdc, adapter!)) > adapterEvmBefore);
                    return;
                }
                case 'flush': {
                    const hash = await writeContract(walletWagmiConfig, {
                        account: eoa,
                        address: manager,
                        abi: HYPER_EVM_MANAGER_ABI,
                        functionName: 'selfFlush',
                        args: [],
                        chainId: HYPER_EVM_CHAIN_ID,
                    });
                    txHashes.push(hash);
                    await waitForSuccessfulHyperEvmReceipt(hash, 'Collect');
                    return;
                }
                }
            };

            for (const [index, step] of plan.steps.entries()) {
                setState((prev) => ({
                    ...prev,
                    status: 'running',
                    stepIndex: index + 1,
                    stepCount: plan.steps.length,
                    stepLabel: DIRECT_STEP_LABELS[step.kind],
                    txHashes: [...txHashes],
                }));
                await executeStep(step);
            }

            // Every transaction of the run is confirmed at this point. A plan that drained
            // HyperCore emptied the account completely, and Hyperliquid PRUNES the agent
            // registration and the builder approval when that happens — invisible to the backend's
            // db-only status read. Tell it, so the Reactivate banner appears before the next trade
            // fails. An EVM-only plan leaves the Core account funded and must NOT signal.
            // Fire-and-forget by design: the withdrawal already succeeded and nothing here may
            // block or fail it; a lost signal is caught later by the order-time error.
            const drainedCore = plan.steps.some(
                (step) =>
                    step.kind === 'corePerpToSpot' || step.kind === 'coreSpotSweep',
            );
            if (drainedCore && authedApi) {
                reportAgentPruned(authedApi, 'fullWithdrawal')
                    .then((status) => {
                        // seed the cache from the response (it IS the status shape) so the
                        // Reactivate banner appears immediately instead of after the 30s staleTime
                        queryClient.setQueryData(
                            queryKeys.speed.agentStatus(),
                            status,
                        );
                    })
                    .catch((error: unknown) => {
                        console.warn('[DirectWithdraw] report-pruned failed:', error);
                    });
            }

            const delivered = (await readEvmUsdc6(usdc, eoa)) - eoaUsdcBefore;
            setState((prev) => ({
                ...prev,
                status: 'success',
                stepLabel: null,
                txHashes: [...txHashes],
                deliveredUsd: Number(formatUnits(delivered > 0n ? delivered : 0n, 6)),
            }));
            toast.success(
                `Withdrew $${Number(formatUnits(delivered > 0n ? delivered : 0n, 6)).toFixed(2)} to your wallet on HyperEVM.`,
                { duration: TOAST_DURATION },
            );
            onSuccess?.();
        } catch (error) {
            const described = describeErrorForUser(error, {
                operation: 'Withdrawal',
            });
            const message = userErrorText(described);
            console.error('[DirectWithdraw] failed:', error);
            // balances may have PARTIALLY moved (each step is its own transaction) — refresh so
            // the panel shows where the funds actually are.
            onSuccess?.();
            setState((prev) => ({
                ...prev,
                status: 'failed',
                error: message,
                txHashes: [...txHashes],
            }));
            toast.error(described.title, {
                description: message,
                duration: TOAST_DURATION,
            });
        } finally {
            lockRef.current = false;
            // best-effort return to the anchor — everything else in the app gates on it.
            // Failure is fine: the next deposit/auth flow runs ensureAnchor anyway.
            switchChain(walletWagmiConfig, { chainId: anchorChainId }).catch(
                () => undefined,
            );
        }
    }, [
        address,
        anchorChainId,
        authedApi,
        factionsApi,
        onSuccess,
        queryClient,
    ]);

    return {
        state,
        run,
        reset,
        isRunning: state.status === 'preparing' || state.status === 'running',
        hypeGasOk,
    };
}
